import { getShopifyProductId, getShopifyShopId } from "@/utils/metadata";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.socialocean.co/api";

declare global {
  interface Window {
    __SOCIAL_OCEAN_RATINGS__?: { total: number };
  }
}

/**
 * Publish the real ratings total for the host theme's star badge.
 *
 * Pure hand-off: announce the number via a global (read synchronously if the
 * theme's count-up loader attaches late) and the `social-ocean:ratings-loaded`
 * event. The theme (snippets/star-review-countup.liquid) owns the badge DOM and
 * the label wording (its own " reviews" suffix) for BOTH arms — animating the
 * number in treatment, writing it as text in control — so the widget never
 * touches `.review-count` itself.
 */
export function publishRatingsTotal(total: number): void {
  if (typeof window === "undefined") return;
  window.__SOCIAL_OCEAN_RATINGS__ = { total };
  window.dispatchEvent(
    new CustomEvent("social-ocean:ratings-loaded", { detail: { total } }),
  );
}

/**
 * Control-arm helper: fetch only the ratings total and publish it to the shared
 * theme star badge, WITHOUT mounting the widget or firing any tracking. The
 * star line is chrome both arms show, so keeping its count accurate removes a
 * confound — only the count-up animation and the review surface differ between
 * control and treatment. Fire-and-forget: on any failure the badge just keeps
 * its server-rendered placeholder.
 */
export async function fetchAndPublishRatingsTotal(): Promise<void> {
  if (typeof window === "undefined") return;
  const productId = getShopifyProductId();
  const shopId = getShopifyShopId();
  if (!productId || !shopId) return;
  try {
    const url = new URL(`${API_BASE_URL}/widget/reviews/${productId}`);
    url.searchParams.set("shop", shopId);
    url.searchParams.set("page", "1");
    url.searchParams.set("per_page", "1"); // we only need the total
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
      credentials: "omit",
    });
    if (!res.ok) return;
    const json = await res.json();
    const total = json?.data?.total;
    if (typeof total === "number" && total > 0) publishRatingsTotal(total);
  } catch {
    /* fire-and-forget: analytics/badge must never break the page */
  }
}
